/**
 * ServiceOrderScheduleTab - Aba Agenda do modal da OS (BR-OS-030)
 *
 * Lista o histórico de agendamentos (Data e Hora, Prestador, Status, Observação,
 * Ações). Apenas o agendamento vigente permite Reagendar e Cancelar. O olho abre
 * o calendário de OS já posicionado no agendamento (BR-OS-031).
 *
 * RELEASE: 2026-10-10 v1.0.0 - Versão inicial (substitui aviso + "Abrir Agenda").
 * RELEASE: 2026-10-10 v1.1.0 - BR-OS-032: editor de agendamento extraído para
 *            `ServiceOrderScheduleDialog` (reuso pelo botão Agenda do cabeçalho).
 * RELEASE: 2026-10-10 v1.2.0 - Badge "Confirmado" passa a azul sólido com letra
 *            branca (`bg-info text-info-foreground`); demais status inalterados.
 * RELEASE: 2026-10-10 v1.3.0 - Tipografia da listagem reduzida ~15% (cabeçalho e
 *            células em text-xs, secundários em text-[10px], badge compacto,
 *            linhas e botões menores), no padrão da tabela de movimentações.
 */
import { SERVICE_ORDER_SCHEDULABLE, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { useState } from 'react';
import { CalendarPlus, Eye, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { InlineNotice } from '@/components/ui/inline-notice';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  SCHEDULE_STATUS_LABEL, canManageSchedule, useCancelServiceOrderSchedule,
  useServiceOrderSchedules, type ServiceOrderSchedule,
} from '@/hooks/useServiceOrderSchedules';
import { ServiceOrderCalendarModal } from '../ServiceOrderCalendarModal';
import { ServiceOrderScheduleDialog } from './ServiceOrderScheduleDialog';

interface Props {
  order: { id: string; status: string; provider_person_id?: string | null; provider_name?: string | null };
  canEdit: boolean;
}

/**
 * Cores do badge de status do agendamento.
 * "Confirmado" (active) usa azul sólido com letra branca (token semântico `info`,
 * que em modo claro é azul com foreground branco), mantendo os demais tons suaves.
 */
const STATUS_CLASS: Record<string, string> = {
  active: 'bg-info text-info-foreground',
  rescheduled: 'bg-muted text-muted-foreground',
  cancelled: 'bg-destructive/10 text-destructive',
  done: 'bg-success/10 text-success',
};

const fmt = (d: string | null) => (d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '—');
const errMsg = (e: unknown) => (e as { message?: string })?.message ?? 'Erro ao salvar';

export function ServiceOrderScheduleTab({ order, canEdit }: Props) {
  const { data: rows = [], isLoading } = useServiceOrderSchedules(order.id);
  const cancel = useCancelServiceOrderSchedule();

  const [calendarAt, setCalendarAt] = useState<ServiceOrderSchedule | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceOrderSchedule | 'new' | null>(null);
  const [cancelling, setCancelling] = useState<ServiceOrderSchedule | null>(null);
  const [reason, setReason] = useState('');

  const canSchedule = canEdit && SERVICE_ORDER_SCHEDULABLE.includes(order.status as ServiceOrderStatus);

  const confirmCancel = () => {
    if (reason.trim().length < 3) return toast.error('Informe o motivo');
    cancel.mutate({ id: order.id, note: reason }, {
      onSuccess: () => { toast.success('Agendamento cancelado'); setCancelling(null); setReason(''); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  if (isLoading) return <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>;

  const hasActive = rows.some((r) => r.status === 'active');

  return (
    <div className="space-y-3">
      {/* Tipografia compacta (-15%): rótulos e botões menores que o padrão das tabelas */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">{rows.length} {rows.length === 1 ? 'agendamento' : 'agendamentos'}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => { setCalendarAt(null); setCalendarOpen(true); }}>
            <Eye className="h-3.5 w-3.5" />Ver calendário
          </Button>
          {canSchedule && !hasActive && (
            <Button size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setEditing('new')}>
              <CalendarPlus className="h-3.5 w-3.5" />Agendar
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <InlineNotice variant="info">Esta OS ainda não tem agendamento.</InlineNotice>
      ) : (
        /* Linhas compactas (sem viewport vertical interno) e cabeçalho menor, no padrão
           da tabela de movimentações de estoque */
        <Table className="[&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Data e Hora</TableHead>
              <TableHead className="text-xs">Prestador</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Observação</TableHead>
              <TableHead className="text-right text-xs">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const manage = canEdit && canManageSchedule(r, order.status);
              return (
                <TableRow key={r.id} className={cn(r.status !== 'active' && 'opacity-60')}>
                  <TableCell>
                    <div className="text-xs font-medium">{fmt(r.scheduled_start)}</div>
                    {r.scheduled_end && <div className="text-[10px] text-muted-foreground">até {format(new Date(r.scheduled_end), 'HH:mm')}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{r.provider_name ?? '—'}</TableCell>
                  <TableCell><Badge variant="secondary" className={cn('h-5 border-0 px-2 text-[10px] font-semibold', STATUS_CLASS[r.status])}>{SCHEDULE_STATUS_LABEL[r.status] ?? r.status}</Badge></TableCell>
                  <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground" title={r.note ?? ''}>{r.note || '—'}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Tooltip><TooltipTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setCalendarAt(r); setCalendarOpen(true); }}><Eye className="h-3.5 w-3.5" /></Button>
                      </TooltipTrigger><TooltipContent>Ver no calendário</TooltipContent></Tooltip>
                      {manage && <>
                        <Tooltip><TooltipTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => setEditing(r)}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        </TooltipTrigger><TooltipContent>Reagendar</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => { setReason(''); setCancelling(r); }}><XCircle className="h-3.5 w-3.5" /></Button>
                        </TooltipTrigger><TooltipContent>Cancelar agendamento</TooltipContent></Tooltip>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Agendar / Reagendar (componente compartilhado com o botão Agenda do cabeçalho) */}
      <ServiceOrderScheduleDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}
        orderId={order.id} orderProviderId={order.provider_person_id} orderProviderName={order.provider_name}
        schedule={editing === 'new' ? null : editing} />

      {/* Cancelar agendamento (OS volta para Aberta) */}
      <Dialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancelar agendamento</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A OS volta para a situação Aberta. Informe o motivo:</p>
          <Textarea maxLength={500} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo do cancelamento" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>Voltar</Button>
            <Button variant="destructive" onClick={confirmCancel} disabled={cancel.isPending}>{cancel.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Cancelar agendamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServiceOrderCalendarModal open={calendarOpen} onOpenChange={setCalendarOpen}
        initialDate={calendarAt?.scheduled_start ?? null} focusServiceOrderId={order.id}
        initialProviderId={null} />
    </div>
  );
}
